import React from 'react';
import { Helmet } from 'react-helmet';
import { Box, Heading, Text, Button } from '@indeed/ifl-components';
import PageTransition from '../../components/PageTransition/PageTransition';

const PostApplyPage: React.FunctionComponent = () => {
    const handleViewJobs = () => {
        // Redirect to main Indeed site after successful application submission
        window.location.href = 'https://qa.indeed.net';
    };

    return (
        <PageTransition pageKey="post-apply">
            <Helmet>
                <title>Application Submitted - Indeed</title>
                <meta
                    name="description"
                    content="Your job application has been successfully submitted. Thank you for applying with Indeed."
                />
                <meta
                    name="keywords"
                    content="application submitted, job application, Indeed, success"
                />
            </Helmet>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minBlockSize: '60vh',
                    textAlign: 'center',
                    maxInlineSize: '600px',
                    margin: '0 auto',
                    padding: 6,
                }}
            >
                {/* Success Icon */}
                <Box
                    sx={{
                        inlineSize: '80px',
                        blockSize: '80px',
                        borderRadius: '50%',
                        backgroundColor: 'success.default',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginBlockEnd: 4,
                    }}
                >
                    <Text sx={{ fontSize: '2rem', color: 'white' }}>✓</Text>
                </Box>

                {/* Success Message */}
                <Heading
                    as="h1"
                    level={1}
                    sx={{
                        color: 'foreground.primary',
                        marginBlockEnd: 3,
                        fontSize: ['1.5rem', '2rem'],
                    }}
                >
                    Application Submitted Successfully!
                </Heading>

                <Text
                    sx={{
                        color: 'foreground.secondary',
                        fontSize: 3,
                        lineHeight: 1.6,
                        marginBlockEnd: 4,
                    }}
                >
                    Thank you for applying! We've received your application and will review it
                    shortly. You'll hear from us within the next few business days.
                </Text>

                {/* Application Details */}
                <Box
                    sx={{
                        backgroundColor: 'background.secondary',
                        padding: 4,
                        borderRadius: 2,
                        marginBlockEnd: 5,
                        inlineSize: '100%',
                    }}
                >
                    <Heading
                        as="h3"
                        level={4}
                        sx={{ color: 'foreground.primary', marginBlockEnd: 3 }}
                    >
                        Application Details
                    </Heading>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Text sx={{ fontWeight: 'bold' }}>Position:</Text>
                            <Text>Senior Full Stack Engineer</Text>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Text sx={{ fontWeight: 'bold' }}>Company:</Text>
                            <Text>Indeed</Text>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Text sx={{ fontWeight: 'bold' }}>Submitted:</Text>
                            <Text>{new Date().toLocaleDateString()}</Text>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Text sx={{ fontWeight: 'bold' }}>Application ID:</Text>
                            <Text sx={{ fontFamily: 'monospace' }}>APP-{Date.now()}</Text>
                        </Box>
                    </Box>
                </Box>

                {/* Next Steps */}
                <Box
                    sx={{
                        backgroundColor: 'primary.subtle',
                        padding: 4,
                        borderRadius: 2,
                        marginBlockEnd: 5,
                        inlineSize: '100%',
                    }}
                >
                    <Heading as="h3" level={4} sx={{ color: 'primary.strong', marginBlockEnd: 3 }}>
                        What's Next?
                    </Heading>
                    <Box as="ul" sx={{ paddingInlineStart: 4, color: 'primary.strong' }}>
                        <Text as="li" sx={{ marginBlockEnd: 2 }}>
                            Our hiring team will review your application
                        </Text>
                        <Text as="li" sx={{ marginBlockEnd: 2 }}>
                            We'll contact you within 3-5 business days
                        </Text>
                        <Text as="li" sx={{ marginBlockEnd: 2 }}>
                            Check your email for updates and next steps
                        </Text>
                        <Text as="li">You can view your application status in your dashboard</Text>
                    </Box>
                </Box>

                {/* Action Buttons */}
                <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', justifyContent: 'center' }}>
                    <Button variant="primary" onClick={handleViewJobs}>
                        View More Jobs
                    </Button>
                </Box>
            </Box>
        </PageTransition>
    );
};

export default PostApplyPage;
